import { createContext, useContext, useEffect, useReducer, useRef, useState, type PointerEvent as RPointerEvent } from 'react';
import type { Editor } from '@tiptap/core';
import DOMPurify from 'dompurify';
import type { Node as PMNode } from '@tiptap/pm/model';
import { NodeViewWrapper, type NodeViewProps } from '@tiptap/react';
import { fmtNum, type FormatoParagrafo } from '../estilos';
import { alturaDoPapel, larguraDoPapel, textoFolha, textoNumeroPagina, type DocumentoForense, type Timbrado } from '../documento';

/* ===================== Contexto da folha ===================== */

export interface ContextoFolha {
  editor: Editor | null;
  documento: DocumentoForense;
  timbrado: Timbrado | null;
  /** O app guarda o texto das notas pela chamada; editar aqui reescreve o atributo. */
  editarNota: (pos: number, texto: string) => void;
  editandoCabecalho: boolean;
  definirCabecalhoRodape: (qual: 'cabecalho' | 'rodape', html: string) => void;
}

export const FolhaContext = createContext<ContextoFolha | null>(null);
const useFolha = () => {
  const ctx = useContext(FolhaContext);
  if (!ctx) throw new Error('FolhaContext ausente');
  return ctx;
};

/** Re-renderiza a cada mudança do documento (notas e páginas mudam com o texto). */
function useAtualizaComEditor(editor: Editor | null) {
  const [, tick] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    if (!editor) return;
    editor.on('update', tick);
    return () => { editor.off('update', tick); };
  }, [editor]);
}

const EH_QUEBRA_DE_PAGINA = (n: PMNode) => n.type.name === 'quebra' && (n.attrs.tipo === 'pagina' || n.attrs.tipo === 'secao');

/** Quantas páginas a peça tem nesta etapa: as quebras manuais + 1. */
export function totalDePaginas(doc: PMNode): number {
  let n = 1;
  doc.forEach((filho) => { if (EH_QUEBRA_DE_PAGINA(filho)) n += 1; });
  return n;
}

/** As notas de cada página, na ordem, com a posição da chamada. */
export function notasPorPagina(doc: PMNode): { pos: number; numero: number; texto: string }[][] {
  const paginas: { pos: number; numero: number; texto: string }[][] = [[]];
  doc.descendants((node, pos) => {
    if (EH_QUEBRA_DE_PAGINA(node)) { paginas.push([]); return false; }
    if (node.type.name === 'nota') paginas[paginas.length - 1].push({ pos, numero: node.attrs.numero, texto: node.attrs.texto });
    return true;
  });
  return paginas;
}

/* ===================== Cabeçalho e rodapé ===================== */

function HtmlEditavel({ html, editavel, aoMudar, className, rotulo }: { html: string; editavel: boolean; aoMudar: (h: string) => void; className: string; rotulo: string }) {
  const ref = useRef<HTMLDivElement>(null);
  // Sanitizado na entrada e na saída: o cabeçalho e o rodapé são texto
  // guardado (e, no servidor, escrito também por quem atua pela conta).
  useEffect(() => {
    const limpo = DOMPurify.sanitize(html);
    if (ref.current && ref.current.innerHTML !== limpo) ref.current.innerHTML = limpo;
  }, [html]);
  return (
    <div
      ref={ref}
      className={className}
      aria-label={rotulo}
      contentEditable={editavel}
      suppressContentEditableWarning
      onBlur={(e) => aoMudar(DOMPurify.sanitize(e.currentTarget.innerHTML))}
    />
  );
}

export function Cabecalho({ indice }: { indice: number }) {
  const { documento, timbrado, editandoCabecalho, definirCabecalhoRodape, editor } = useFolha();
  useAtualizaComEditor(editor);
  const total = editor ? totalDePaginas(editor.state.doc) : 1;
  const numero = documento.paginas.posicao === 'cabecalhoDireita' ? textoNumeroPagina(documento, indice, total) : null;
  const fls = textoFolha(documento, indice);
  const t = timbrado;
  const marca = t?.marcaDagua?.trim();
  return (
    <div className="cm-ef-cab-folha">
      {/* A marca d'água da página, a 42% da altura do papel, como no PDF (minuta_editor.html);
          o começo da página fica 1,1 cm acima do cabeçalho. */}
      {marca && <span className="cm-ef-marca-dagua" aria-hidden style={{ top: `${(alturaDoPapel(documento) * 0.42 - 1.1).toFixed(2)}cm` }}>{marca}</span>}
      {fls && <span className="cm-ef-fls">{fls}</span>}
      <div className="cm-ef-cabecalho">
        {t?.modo === 'imagem' && t.imagemCabecalho ? (
          <img src={t.imagemCabecalho} alt="" className="cm-ef-timbre-img" />
        ) : t ? (
          <MarcaDoTimbrado t={t} />
        ) : null}
        <HtmlEditavel
          className={`cm-ef-hf-livre${editandoCabecalho ? ' is-editando' : ''}`}
          html={documento.cabecalho}
          editavel={editandoCabecalho}
          aoMudar={(h) => definirCabecalhoRodape('cabecalho', h)}
          rotulo="Texto do cabeçalho"
        />
        {numero && <span className="cm-ef-num-pag">{numero}</span>}
      </div>
    </div>
  );
}

export function Rodape({ indice }: { indice: number }) {
  const { documento, timbrado, editandoCabecalho, definirCabecalhoRodape, editor, editarNota } = useFolha();
  useAtualizaComEditor(editor);
  const total = editor ? totalDePaginas(editor.state.doc) : 1;
  const notas = editor ? notasPorPagina(editor.state.doc)[indice] ?? [] : [];
  const numero = documento.paginas.posicao !== 'cabecalhoDireita' ? textoNumeroPagina(documento, indice, total) : null;
  const t = timbrado;
  return (
    <div className="cm-ef-pe-folha">
      {notas.length > 0 && (
        <ol className="cm-ef-notas" aria-label="Notas de rodapé">
          {notas.map((n) => (
            <li key={n.pos}>
              <sup>{n.numero}</sup>
              <NotaEditavel texto={n.texto} aoMudar={(texto) => editarNota(n.pos, texto)} />
            </li>
          ))}
        </ol>
      )}
      <div className={`cm-ef-rodape${documento.paginas.posicao === 'rodapeCentro' ? ' is-centro' : ''}`}>
        {t?.modo === 'imagem' && t.imagemRodape ? (
          <img src={t.imagemRodape} alt="" className="cm-ef-timbre-img" />
        ) : t ? (
          <span className="cm-ef-tb-texto">{textoDoRodape(t)}</span>
        ) : null}
        <HtmlEditavel
          className={`cm-ef-hf-livre${editandoCabecalho ? ' is-editando' : ''}`}
          html={documento.rodape}
          editavel={editandoCabecalho}
          aoMudar={(h) => definirCabecalhoRodape('rodape', h)}
          rotulo="Texto do rodapé"
        />
        {numero && <span className="cm-ef-num-pag">{numero}</span>}
      </div>
    </div>
  );
}

function NotaEditavel({ texto, aoMudar }: { texto: string; aoMudar: (t: string) => void }) {
  const [valor, setValor] = useState(texto);
  const [textoVisto, setTextoVisto] = useState(texto);
  if (texto !== textoVisto) { setTextoVisto(texto); setValor(texto); }
  return (
    <span
      className="cm-ef-nota-texto"
      contentEditable
      suppressContentEditableWarning
      role="textbox"
      aria-label="Texto da nota"
      data-vazia={!valor || undefined}
      onInput={(e) => setValor(e.currentTarget.textContent ?? '')}
      onBlur={() => { if (valor !== texto) aoMudar(valor); }}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); (e.currentTarget as HTMLElement).blur(); } }}
    >
      {texto}
    </span>
  );
}

/* ===================== Quebra (node view) ===================== */

/**
 * Como a quebra aparece na tela. A de página desenha o fim de uma folha (as
 * notas e o rodapé dela), o vão da mesa e o começo da seguinte (cabeçalho e
 * folhas). A de coluna e a de seção contínua são só uma linha com rótulo.
 */
export function QuebraView({ node, getPos, editor, selected }: NodeViewProps) {
  useAtualizaComEditor(editor);
  const tipo = node.attrs.tipo as string;
  if (tipo === 'coluna' || tipo === 'secaoContinua') {
    return (
      <NodeViewWrapper className={`cm-ef-quebra-linha${selected ? ' is-sel' : ''}`} data-quebra={tipo} contentEditable={false}>
        <span>{tipo === 'coluna' ? 'quebra de coluna' : 'quebra de seção (contínua)'}</span>
      </NodeViewWrapper>
    );
  }
  // Índice desta quebra entre as de página: é a página que ela fecha.
  const pos = typeof getPos === 'function' ? getPos() : 0;
  let indice = 0;
  editor.state.doc.forEach((filho, desloc) => { if (desloc < (pos ?? 0) && EH_QUEBRA_DE_PAGINA(filho)) indice += 1; });
  return (
    <NodeViewWrapper className={`cm-ef-quebra-pagina${selected ? ' is-sel' : ''}`} data-quebra={tipo} contentEditable={false}>
      <Rodape indice={indice} />
      <div className="cm-ef-vao" aria-hidden>{tipo === 'secao' ? 'quebra de seção' : 'quebra de página'}</div>
      <Cabecalho indice={indice + 1} />
    </NodeViewWrapper>
  );
}

/* ===================== Régua ===================== */

export const PX_CM = 37.8;

/** Logo + identificação do timbrado — a mesma na folha e na prévia do painel. */
export function MarcaDoTimbrado({ t }: { t: Timbrado }) {
  // No modo imagem sem a faixa do cabeçalho, o texto dele (o arquivo pode ter só a do rodapé).
  if (t.modo !== 'dados') {
    const [primeira, ...resto] = (t.cabecalhoTexto ?? '').split('\n');
    return (
      <span className="cm-ef-marca">
        {t.logotipo && <img src={t.logotipo} alt="" />}
        <span className="cm-ef-tb-texto"><strong>{primeira}</strong>{resto.join('\n')}</span>
      </span>
    );
  }
  return (
    <span className="cm-ef-marca">
      {t.logotipo && <img src={t.logotipo} alt="" />}
      <span><strong>{t.nome}</strong>{t.oab}</span>
    </span>
  );
}

export function textoDoRodape(t: Timbrado): string {
  return t.modo !== 'dados' ? (t.rodapeTexto ?? '') : [t.endereco, t.contato].filter(Boolean).join(' – ');
}

export function Regua({ documento, formato, aoAjustar, zoom = 1 }: { documento: DocumentoForense; formato: FormatoParagrafo; aoAjustar: (a: Partial<FormatoParagrafo>) => void; zoom?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [arraste, setArraste] = useState<null | { tipo: 'primeira' | 'esq' | 'dir'; x: number }>(null);
  const largura = larguraDoPapel(documento);
  const { esquerda: mEsq, direita: mDir } = documento.margens;
  const util = largura - mEsq - mDir;
  const f = formato;
  const xEsq = (mEsq + f.recuoEsquerdo) * PX_CM;
  const xPrimeira = f.especial === 'primeira' ? xEsq + f.medida * PX_CM : xEsq;
  const xCorpo = f.especial === 'deslocado' ? xEsq + f.medida * PX_CM : xEsq;
  const xDir = (largura - mDir - f.recuoDireito) * PX_CM;

  const mover = (e: RPointerEvent) => {
    if (!arraste || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    // Com zoom, a distância na tela é a da régua vezes o zoom.
    const px = (e.clientX - r.left) / zoom;
    const cm = Math.round((px / PX_CM) * 4) / 4;
    setArraste({ ...arraste, x: px });
    if (arraste.tipo === 'esq') aoAjustar({ recuoEsquerdo: Math.max(0, Math.min(10, cm - mEsq - (f.especial === 'deslocado' ? f.medida : 0))) });
    else if (arraste.tipo === 'dir') aoAjustar({ recuoDireito: Math.max(0, Math.min(8, largura - mDir - cm)) });
    else {
      const d = cm - mEsq - f.recuoEsquerdo;
      if (Math.abs(d) < 0.01) aoAjustar({ especial: 'nenhum', medida: 0 });
      else if (d > 0) aoAjustar({ especial: 'primeira', medida: Math.min(8, d) });
      else aoAjustar({ especial: 'deslocado', medida: Math.min(f.recuoEsquerdo, -d) });
    }
  };
  const marcador = (tipo: 'primeira' | 'esq' | 'dir', x: number, titulo: string) => (
    <span
      className={`cm-ef-marcador cm-ef-m-${tipo}`}
      style={{ left: x }}
      title={titulo}
      role="slider"
      aria-label={titulo}
      aria-valuenow={tipo === 'esq' ? f.recuoEsquerdo : tipo === 'dir' ? f.recuoDireito : f.medida}
      onPointerDown={(e) => { e.preventDefault(); (e.target as HTMLElement).setPointerCapture(e.pointerId); setArraste({ tipo, x }); }}
    >
      <svg viewBox="0 0 14 10" aria-hidden>{tipo === 'primeira' ? <path d="M0 0h14L7 10z" /> : <path d="M7 0l7 10H0z" />}</svg>
    </span>
  );
  const dica = arraste
    ? arraste.tipo === 'esq' ? `Esquerda ${fmtNum(f.recuoEsquerdo)} cm`
      : arraste.tipo === 'dir' ? `Direita ${fmtNum(f.recuoDireito)} cm`
        : f.especial === 'nenhum' ? 'Sem recuo especial' : `${f.especial === 'primeira' ? '1ª linha' : 'Deslocado'} ${fmtNum(f.medida)} cm`
    : null;

  return (
    <div className="cm-ef-regua-wrap">
      <div
        ref={ref}
        className="cm-ef-regua"
        style={{ width: largura * PX_CM, ...(zoom !== 1 ? { zoom } : {}) }}
        onPointerMove={mover}
        onPointerUp={() => setArraste(null)}
        onPointerCancel={() => setArraste(null)}
        aria-label="Régua: arraste os marcadores para mudar os recuos do parágrafo"
      >
        <div className="cm-ef-regua-trilho" />
        <div className="cm-ef-regua-margem" style={{ left: 0, width: mEsq * PX_CM }} />
        <div className="cm-ef-regua-margem" style={{ right: 0, width: mDir * PX_CM }} />
        {Array.from({ length: Math.floor(largura * 2) + 1 }, (_, i) => {
          const c = i / 2;
          const rel = c - mEsq;
          return (
            <span key={i}>
              <span className={`cm-ef-tique${c % 1 === 0 ? ' is-inteira' : ''}`} style={{ left: c * PX_CM }} />
              {c % 1 === 0 && rel > 0 && rel < util && rel % 2 === 0 && <span className="cm-ef-num-cm" style={{ left: c * PX_CM }}>{rel}</span>}
            </span>
          );
        })}
        {marcador('primeira', xPrimeira, 'Recuo da primeira linha')}
        {marcador('esq', xCorpo, 'Recuo à esquerda')}
        {marcador('dir', xDir, 'Recuo à direita')}
        {dica && <span className="cm-ef-regua-dica" style={{ left: arraste!.x }}>{dica}</span>}
      </div>
    </div>
  );
}
